// React-side error tracking (PRD #35 "Observability", issue #43), DSN-gated
// exactly like Matomo. The server describes the project in a `meta[name="sentry"]`
// tag (ApplicationController#sentry_settings) which the client reads; the SSR
// (Node sidecar) branch reads the same values from the container env it inherits
// at runtime. Without a DSN nothing initialises — the build is byte-for-byte
// unchanged. Telemetry must never break the page, so init is wrapped defensively
// (cf. lib/funnel.ts). The frontend has its own Sentry project/DSN, separate
// from the Rails SDK's SENTRY_DSN.
import * as Sentry from '@sentry/react'

type SentrySettings = {
  dsn: string
  release: string | null
  environment: string
}

// Node's `process` global — reached through globalThis so no @types/node is
// needed and the reference is harmless in the browser (where it's undefined).
type NodeProcess = { env?: Record<string, string | undefined> }

// Noise from code we don't ship, injected into the page by the visitor's own
// browser. Extend only with things PROVEN to originate outside our bundle — a
// filter here is invisible, so anything added must be justified in the comment.
const THIRD_PARTY_NOISE = [
  // "Object Not Found Matching Id:2, MethodName:update, ParamCount:4" — the
  // error format of a native-messaging bridge injected by fingerprint-spoofing
  // browser extensions, not JavaScript's own. Seen 2026-07-28 from a visitor
  // whose breadcrumbs logged "antifingerprint not defined yet" and whose
  // reported timezone (America/Los_Angeles) contradicted their geography (GB) —
  // i.e. the extension was actively spoofing. It arrives as an unhandled
  // rejection of a non-Error value, with no frame in our code.
  //
  // Sentry's own default ignore list already carries this exact family, but
  // only pinned to `MethodName:simulateEvent`; the MethodName varies, so match
  // the format instead of one method.
  /Object Not Found Matching Id:\d+, MethodName:\w+/,

  // "Invalid call to runtime.sendMessage(). Tab not found." — a browser
  // extension messaging its own background script after the tab it wanted has
  // gone. `runtime.sendMessage` is the WebExtensions API; iOS Safari extensions
  // (content blockers, password managers, translators) raise this routinely
  // when a tab is backgrounded.
  //
  // PROVEN NOT OURS, which is this list's bar: the string appears nowhere in
  // app/frontend, and nowhere in @sentry or @inertiajs either. We have no
  // reason to ever call it, which is also why this narrow pattern cannot
  // swallow a real fault of ours.
  //
  // Seen 2026-08-19, Mobile Safari 26.6 / iOS 18.7, Croydon GB. Worth knowing
  // for the next one: the breadcrumbs showed a COMPLETELY HEALTHY session —
  // Inertia Navigation, then two UI clicks on the mobile Menu toggle ten
  // seconds apart, then this error nine seconds after the last one. The app
  // hydrated and worked. A UK visitor in this list is NOT evidence for the
  // separate `Page not found: <name>` resolver issue, which is a mount failure
  // and would have no Navigation or UI Click breadcrumbs at all.
  /Invalid call to runtime\.sendMessage\(\)/,
]

function start(dsn: string, release: string | null, environment: string): void {
  Sentry.init({
    dsn,
    // = the deployed commit SHA, so errors map to a release.
    release: release ?? undefined,
    environment,
    // Errors only — performance tracing off (SaaS free tier); no Session Replay
    // (PII + quota).
    tracesSampleRate: 0,
    // Appended to the SDK's defaults, not replacing them.
    ignoreErrors: THIRD_PARTY_NOISE,
  })
}

export function initSentry(): void {
  try {
    if (typeof window === 'undefined') {
      // SSR: the puma :inertia_ssr node process inherits the container env
      // (SENTRY_FRONTEND_DSN, KAMAL_VERSION) at runtime.
      const env = (globalThis as { process?: NodeProcess }).process?.env
      const dsn = env?.SENTRY_FRONTEND_DSN
      if (!dsn) return
      start(dsn, env?.KAMAL_VERSION ?? null, env?.SENTRY_ENVIRONMENT ?? 'production')
      return
    }

    // Client: read the server-rendered meta tag (the Matomo pattern).
    const meta = document.querySelector<HTMLMetaElement>('meta[name="sentry"]')
    if (!meta?.content) return

    let settings: SentrySettings
    try {
      settings = JSON.parse(meta.content) as SentrySettings
    } catch {
      return // a malformed tag disables error tracking, never the page
    }
    if (!settings.dsn) return

    start(settings.dsn, settings.release, settings.environment)
  } catch (error) {
    // Observability setup must never crash the app (cf. lib/funnel.ts) — but a
    // silent failure is invisible, so surface it to the console.
    console.error('[sentry] init failed', error)
  }
}
